import { useMemo, useState } from 'react';

import type { IdAdMediumDto } from '@/services/ws-print/src/main/resources/rest-api';
import { getAdMediums } from '@/services/ws-print/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY, DROP_DOWN_ENTITY_LIMIT } from 'src/constants';
import type { Option } from 'src/types';

type UsePrintAdMediumOptionsReturn = {
  options: Option<IdAdMediumDto>[];
  pending: boolean;
  total: number;
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
};

export const usePrintAdMediumOptions = (): UsePrintAdMediumOptionsReturn => {
  const [searchFilter, setSearchFilter] = useState<string>('');

  const getAdMediumListRequest = useRequest({
    apiMethod: getAdMediums,
    params: { filter: searchFilter, limit: DROP_DOWN_ENTITY_LIMIT, page: 0 },
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    getAdMediumListRequest.data$,
    (response) =>
      response?.data.items.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? [],
  );
  const total = useSubscribe(getAdMediumListRequest.data$, (result) => result?.data?.total ?? 0);
  const pending = useSubscribe(getAdMediumListRequest.pending$);

  return useMemo(
    () => ({
      options,
      pending,
      total,
      searchFilter,
      setSearchFilter,
    }),
    [options, pending, total, searchFilter],
  );
};
